import type { LaneId, VisibleCardViewV1, VisibleLaneV1 } from "@fate-kingdom/engine";
import type { PointerEventHandler } from "react";

import type { AlphaAttackAction } from "../drag/useAlphaFormationDrag";
import type { AlphaDisbandAction } from "../drag/useAlphaFieldDisbandDrag";
import { AlphaGameIcon } from "../icons/AlphaGameIcon";
import { AlphaFieldCommanderCard, AlphaStackedArmyCard } from "./AlphaFieldCard";

type VisibleFormationSide = VisibleLaneV1["own"] | VisibleLaneV1["opponent"];

function readinessLabel(side: VisibleFormationSide): "Garrison" | "Preparing" | "Ready" | "Spent" {
  if (side.commander === null) return "Garrison";
  if (side.formation?.preparing) return "Preparing";
  if (side.formation?.attackedThisTurn || side.formation?.commanderReadiness === "spent")
    return "Spent";
  return "Ready";
}

export function FormationStack({
  laneId,
  side,
  owner,
  attackActions,
  disbandActionsByCardId,
  selected = false,
  state,
  disabled = false,
  onSelectAttack,
  onPointerDown,
  onInspect,
}: {
  readonly laneId: LaneId;
  readonly side: VisibleFormationSide;
  readonly owner: "own" | "opponent";
  readonly attackActions?: ReadonlyMap<LaneId, AlphaAttackAction> | undefined;
  readonly disbandActionsByCardId?: ReadonlyMap<string, AlphaDisbandAction> | undefined;
  readonly selected?: boolean;
  readonly state: "active" | "committed" | "defeated";
  readonly disabled?: boolean;
  readonly onSelectAttack?: () => void;
  readonly onPointerDown?: PointerEventHandler<HTMLButtonElement> | undefined;
  readonly onInspect: (cards: readonly VisibleCardViewV1[]) => void;
}) {
  const cards = [
    ...(side.commander === null ? [] : [side.commander]),
    ...side.armies,
  ] satisfies readonly VisibleCardViewV1[];
  if (cards.length === 0) {
    return (
      <div className={`alpha-formation alpha-formation--${owner} is-empty`} aria-hidden="true">
        <span>Formation slot</span>
      </div>
    );
  }

  const formation = side.formation;
  const readiness = readinessLabel(side);
  const hasAttack = attackActions !== undefined && attackActions.size > 0;
  const hasDisband = disbandActionsByCardId !== undefined && disbandActionsByCardId.size > 0;
  const label = `${laneId} formation. ${String(formation?.totalAttack ?? 0)} Attack, ${String(formation?.totalGuard ?? 0)} Guard, ${String(side.armies.length)} Armies, ${readiness}.`;

  return (
    <section
      className={`alpha-formation alpha-formation--${owner} alpha-formation--${state} ${selected ? "is-selected" : ""} ${hasAttack ? "is-attack-ready" : ""}`}
      aria-label={label}
    >
      <button
        className="alpha-formation-attack"
        type="button"
        data-alpha-formation-source={hasAttack ? laneId : undefined}
        disabled={disabled || (!hasAttack && !hasDisband)}
        aria-label={
          hasAttack
            ? `Attack with ${laneId} formation. ${String(attackActions.size)} legal target${attackActions.size === 1 ? "" : "s"}.`
            : hasDisband
              ? `${label} Select to inspect. Drag a field card to your discard pile to disband it.`
              : label
        }
        aria-pressed={hasAttack ? selected : undefined}
        onClick={hasAttack ? onSelectAttack : hasDisband ? () => onInspect(cards) : undefined}
        onPointerDown={onPointerDown}
      >
        <span className="alpha-formation__army-fan" aria-hidden="true">
          {side.armies.map((army, index) => (
            <span
              className="alpha-formation__army"
              key={army.cardInstanceId}
              style={{ "--alpha-army-index": index } as React.CSSProperties}
            >
              <AlphaStackedArmyCard army={army} index={index} state={state} />
            </span>
          ))}
        </span>
        {side.commander === null ? (
          <span className="alpha-formation__garrison">
            <AlphaGameIcon name="army" aria-hidden="true" />
            Army garrison
          </span>
        ) : (
          <span className="alpha-formation__commander" aria-hidden="true">
            <AlphaFieldCommanderCard commander={side.commander} state={state} />
          </span>
        )}
        <span className="alpha-formation__stats">
          <b>
            <AlphaGameIcon name="attack" aria-hidden="true" />
            {formation?.totalAttack ?? 0}
            <small>ATK</small>
          </b>
          <b>
            <AlphaGameIcon name="guard" aria-hidden="true" />
            {formation?.totalGuard ?? 0}
            <small>GRD</small>
          </b>
          <span>
            <AlphaGameIcon name="army" aria-hidden="true" />
            {formation?.armyCount ?? side.armies.length}
          </span>
          <span>
            <AlphaGameIcon name="size" aria-hidden="true" />
            {formation?.armySize ?? 0}
          </span>
          <em className={`is-${readiness.toLowerCase()}`}>{readiness}</em>
        </span>
      </button>
      <button
        className="alpha-formation__inspect"
        type="button"
        aria-label={`Inspect ${laneId} ${owner === "own" ? "your" : "opponent"} formation cards`}
        onClick={() => onInspect(cards)}
      >
        <AlphaGameIcon name="inspect" aria-hidden="true" />
      </button>
    </section>
  );
}
